import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Grid, Button, TextareaAutosize } from '@mui/material';
import Spinner from '../Spinner';
import { useToast } from '../Toast';
import UserDot from './UserDot';
import axios from '../../../_utils/axios';
import { convertStylesToStyled } from '@/_utils/utils';
import { Note } from '@/redux/slices/slice_types';

interface NoteReplyProps {
  taskId: string;
  addNewNote: (note: Note) => void;
  firstNote: boolean;
  baseUrl: string;
}

const NoteReply = ({ taskId, addNewNote, firstNote, baseUrl }: NoteReplyProps) => {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { error } = useToast();

  const onInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // we don't have a character limit yet,
    // but just to avoid insanely long messages being subbmit
    if (message.length < 8000) {
      setMessage(e.target.value);
    }
  };

  const onSubmit = () => {
    if (message.length > 1) {
      setSubmitting(true);
      axios
        .post(`/${baseUrl}/${taskId}/comments/create/`, { message })
        .then((res) => {
          if (!res.data?.success) throw new Error();
          addNewNote(res.data?.data);
          setMessage('');
        })
        .catch(() => error('There was a problem submitting your note.'))
        .then(() => setSubmitting(false));
    }
  };

  return (
    <StyledForm onSubmit={onSubmit} firstNote={firstNote} isSubmitting={submitting}>
      <Grid container>
        <StyledUserDiv className="user-div">
          <UserDot />
        </StyledUserDiv>
        <StyledReplyBox>
          {submitting && <Spinner size={30} />}
          <StyledTextareaAutosize
            data-testid="reply-text"
            minRows={2}
            maxRows={20}
            placeholder={`Type your ${firstNote ? 'comment' : 'reply'}`}
            className="replyTextarea"
            value={message}
            onChange={onInputChange}
          />
          <StyledReplyButton
            data-testid="submit-btn"
            role="submit"
            size="small"
            variant="outlined"
            onClick={onSubmit}
            disabled={message.length < 2 || submitting}
          >
            {firstNote ? 'comment' : 'reply'}
          </StyledReplyButton>
        </StyledReplyBox>
      </Grid>
    </StyledForm>
  );
};

export default NoteReply;

const StyledReplyBox = styled.div(
  ({ theme }) => `
  position: relative;
  flex: auto;
  border-radius: ${theme.shape.borderRadius}px;
  border: 1px solid ${theme.palette.action.selected};
  padding: ${theme.spacing(2, 0, 0, 2)};
  margin-bottom: ${theme.spacing(3)};
  margin-top: ${theme.spacing(3)};
`
);

const StyledUserDiv = styled.div(
  ({ theme }) => `
  margin-top: ${theme.spacing(1)};
  justify-content: flex-end;
  font-size: 1.25rem;
  padding-right: ${theme.spacing(3)};

  ${theme.breakpoints.up('sm')} {
    width: 178px;
    display: flex;
  }
`
);

type StyledFormProps = {
  firstNote: boolean;
  isSubmitting: boolean;
};

const StyledForm = styled.form(
  ({ firstNote, isSubmitting }: StyledFormProps) => `
  ${
    firstNote
      ? `
      & {
        width: 80%;
        margin: auto;
      }
      & .user-div {
        width: auto;
      }
  `
      : null
  };

  ${
    isSubmitting
      ? `
    & .replyTextarea {
      opacity: 0.2;
      pointer-events: none;
    }
  `
      : null
  };
`
);

const StyledTextareaAutosize = styled(TextareaAutosize)(
  ({ theme }) => `
&& {
  font-family: ${theme.typography.fontFamily};
  background: none;
  border-color: transparent;
  width: 100%;
  font-size: 1rem;
  line-height: 1.5;
  padding-bottom: 50px;
  color: ${theme.palette.text.primary};

  &:focus {
    outline: 0;
  }
}
`
);

const StyledReplyButton = styled(Button)(
  ({ theme }) => `
  && {
    position: absolute;
    height: 30px;
    padding: ${theme.spacing(0.5)};
    right: ${theme.spacing(2)}};
    bottom: ${theme.spacing(2)};
    ${convertStylesToStyled(theme.customOverrides?.TaskNoteReplyButton)}
  }
`
);
